import { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
    BellCheckIcon,
    BellOffIcon,
    CheckIcon,
    CheckCheckIcon,
    CircleIcon,
    EllipsisIcon,
    FileTextIcon,
    InboxIcon,
    LoaderCircleIcon,
    Trash2Icon,
    XIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { env } from '@campai/lib-web/env'
import { notificationCategoriesByTarget, type NotificationCategory } from '@campai/lib-common/entities'
import { formatDateTime } from '@campai/lib-common/i18n'
import { showWebPushPermissionDialog } from '@campai/lib-web/utils/webPush'
import { Markdown, useQueryExt } from '@campai/lib-web/components'
import {
    Badge,
    Button,
    buttonVariants,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Item,
    ItemActions,
    ItemContent,
    ItemDescription,
    ItemFooter,
    ItemGroup,
    ItemTitle,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@campai/lib-web/components/new/ui'
import { useNavigate } from '@campai/lib-web/routes'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

import { appState } from '@/appState'

type NotificationRow = RouterOutput['currentUser']['notifications']['listNotifications']['notifications'][0]
type NotificationLinkRow = NonNullable<NotificationRow['link']>

const categories = notificationCategoriesByTarget.root
const allCategoriesValue = 'all'

type SelectedCategory = NotificationCategory | typeof allCategoriesValue

const getNotificationId = (notification: NotificationRow) => notification._id.toString()

const isNotificationCategory = (value: string): value is NotificationCategory => {
    return categories.some((category) => category === value)
}

const getRootPath = (to: string) => {
    const url = new URL(to, window.location.origin)
    const rootOrigin = new URL(env.VITE_PRODUCT_URL_ROOT).origin

    if (url.origin !== rootOrigin && url.origin !== window.location.origin) {
        return null
    }

    return `${url.pathname}${url.search}${url.hash}`
}

type NotificationItemProps = {
    notification: NotificationRow
    onActivateLink: (
        notification: NotificationRow,
        link: NotificationLinkRow,
        markRead: boolean
    ) => void | Promise<void>
    onChangeReadStatus: (notification: NotificationRow, read: boolean) => void | Promise<void>
    onDelete: (notification: NotificationRow) => void | Promise<void>
}

const NotificationDetailsDialog = ({
    notificationId,
    lastActivityAtMs,
    target,
    onOpenChange,
}: {
    notificationId: string
    lastActivityAtMs: number
    target: NotificationRow['target']
    onOpenChange: (open: boolean) => void
}) => {
    const { t } = useTranslation()
    const { result: notification, isLoading } = useQueryExt(
        ({ signal }) =>
            client.currentUser.notifications.getNotification.query({ notificationId, targets: [target] }, { signal }),
        [notificationId, target, lastActivityAtMs]
    )

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[min(42rem,calc(100vh-2rem))] grid-cols-none flex-col gap-4 sm:max-w-2xl">
                <DialogHeader className="shrink-0">
                    <DialogTitle>{t('notifications.panel.detailsDialog.title')}</DialogTitle>
                </DialogHeader>
                <div className="min-h-0 overflow-y-auto">
                    {isLoading ? (
                        <div className="flex min-h-40 items-center justify-center">
                            <LoaderCircleIcon className="text-muted-foreground size-5 animate-spin" aria-hidden />
                        </div>
                    ) : (
                        <Markdown>{notification?.details ?? ''}</Markdown>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

const NotificationItem = observer(
    ({ notification, onActivateLink, onChangeReadStatus, onDelete }: NotificationItemProps) => {
        const { i18n, t } = useTranslation()
        const i18nKey = 'notifications.panel'
        const isUnread = !notification.readAt
        const link = notification.link
        const canActivate = !!link || notification.hasDetails
        const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false)
        const handleItemActivation = () => {
            if (link) {
                void onActivateLink(notification, link, true)
                return
            }

            if (notification.hasDetails) {
                setIsDetailsDialogOpen(true)
            }
        }

        const item = (
            <Item
                role="listitem"
                size="sm"
                variant="outline"
                tabIndex={canActivate ? 0 : undefined}
                className={classNames('relative items-start', {
                    'hover:bg-muted/50 cursor-pointer': canActivate,
                })}
                onClick={canActivate ? handleItemActivation : undefined}
                onKeyDown={
                    canActivate
                        ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  handleItemActivation()
                              }
                          }
                        : undefined
                }
            >
                <ItemContent className="min-w-0">
                    <div className="flex min-w-0 items-start justify-between gap-3">
                        <ItemTitle className="line-clamp-2 w-auto">
                            <span
                                aria-hidden="true"
                                className={classNames('mr-1.5 inline-block', {
                                    grayscale: !isUnread,
                                })}
                            >
                                {notification.emoji}
                            </span>
                            {notification.title}
                        </ItemTitle>
                        {isUnread && (
                            <Badge color="blue" className="shrink-0">
                                {t(`${i18nKey}.unread`)}
                            </Badge>
                        )}
                    </div>
                    <ItemDescription className="line-clamp-3">{notification.message}</ItemDescription>
                </ItemContent>
                <ItemActions className="absolute bottom-1 right-1">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <EllipsisIcon />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {isUnread ? (
                                <DropdownMenuItem
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        void onChangeReadStatus(notification, true)
                                    }}
                                >
                                    <CheckIcon />
                                    {t(`${i18nKey}.markRead`)}
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        void onChangeReadStatus(notification, false)
                                    }}
                                >
                                    <CircleIcon />
                                    {t(`${i18nKey}.markUnread`)}
                                </DropdownMenuItem>
                            )}
                            {notification.hasDetails && (
                                <DropdownMenuItem
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setIsDetailsDialogOpen(true)
                                    }}
                                >
                                    <FileTextIcon />
                                    {t(`${i18nKey}.showDetails`)}
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                variant="destructive"
                                onClick={(event) => {
                                    event.stopPropagation()
                                    void onDelete(notification)
                                }}
                            >
                                <Trash2Icon />
                                {t(`${i18nKey}.delete`)}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </ItemActions>
                <ItemFooter className="text-muted-foreground mt-1 justify-start text-xs">
                    {notification.lastActivityAt && <span>{formatDateTime(notification.lastActivityAt, { i18n })}</span>}
                </ItemFooter>
            </Item>
        )

        return (
            <>
                {item}
                {isDetailsDialogOpen && (
                    <NotificationDetailsDialog
                        notificationId={getNotificationId(notification)}
                        lastActivityAtMs={notification.lastActivityAt.getTime()}
                        target={notification.target}
                        onOpenChange={setIsDetailsDialogOpen}
                    />
                )}
            </>
        )
    }
)

export const NotificationsPanel = observer(() => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const i18nKey = 'notifications.panel'
    const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>(allCategoriesValue)
    const [isMarkingAllRead, setIsMarkingAllRead] = useState(false)
    const [isRegisteringWebPush, setIsRegisteringWebPush] = useState(false)
    const selectedCategories = useMemo(
        () => (selectedCategory === allCategoriesValue ? categories : [selectedCategory]),
        [selectedCategory]
    )
    const selectedCategoryLabel =
        selectedCategory === allCategoriesValue
            ? t(`${i18nKey}.all`)
            : t(`entities.notification.category.${selectedCategory}`)
    const isNotificationsVisible = appState.isNotificationsVisible
    const notificationsState = appState.notifications

    useEffect(() => {
        if (isNotificationsVisible) {
            setSelectedCategory(allCategoriesValue)
            appState.updateWebPushPermission()
        }
    }, [isNotificationsVisible])

    useEffect(() => {
        if (!isNotificationsVisible) {
            notificationsState.stop()
            return
        }

        notificationsState.start({
            categories: selectedCategories,
            organizationId: null,
        })

        return () => notificationsState.stop()
    }, [isNotificationsVisible, notificationsState, selectedCategories])

    const notifications = notificationsState.data || []
    const { count, hasMore, isLoading } = notificationsState

    const handleSelectedCategoryChange = (value: SelectedCategory | null) => {
        if (!value) {
            return
        }

        if (value === allCategoriesValue || isNotificationCategory(value)) {
            setSelectedCategory(value)
        }
    }

    const changeReadStatus = async (notification: NotificationRow, read: boolean) => {
        const notificationId = getNotificationId(notification)

        await (read
            ? client.currentUser.notifications.markNotificationRead.mutate({ notificationId, through: notification.lastActivityAt })
            : client.currentUser.notifications.markNotificationUnread.mutate({ notificationId }))

        notificationsState.updateData({
            newData: notifications.map((item) =>
                getNotificationId(item) === notificationId ? { ...item, readAt: read ? new Date() : null } : item
            ),
        })
    }

    const deleteNotification = async (notification: NotificationRow) => {
        await client.currentUser.notifications.deleteNotification.mutate({
            notificationId: getNotificationId(notification),
            through: notification.lastActivityAt,
        })

        void notificationsState.reload()
    }

    const markAllRead = async () => {
        setIsMarkingAllRead(true)

        try {
            await client.currentUser.notifications.markAllNotificationsRead.mutate({
                categories: selectedCategories,
                organizationId: null,
                targets: ['root'],
            })
            void notificationsState.reload()
        } finally {
            setIsMarkingAllRead(false)
        }
    }

    const activateRootLink = (to: string) => {
        const path = getRootPath(to)

        if (!path) {
            window.location.href = to
            return
        }

        navigate({ to: path })
    }

    const activateLink = async (notification: NotificationRow, link: NotificationLinkRow, markRead: boolean) => {
        if (markRead && !notification.readAt) {
            await changeReadStatus(notification, true)
        }

        if (link.external) {
            window.open(link.to, '_blank', 'noopener,noreferrer')
            return
        }

        activateRootLink(link.to)
    }

    const handleWebPushClick = async () => {
        appState.updateWebPushPermission()
        const permission = appState.webPushPermission

        if (permission === 'granted') {
            return
        }

        const confirmed = await showWebPushPermissionDialog(permission)

        if (confirmed) {
            await requestWebPushPermission()
        }
    }

    const requestWebPushPermission = async () => {
        setIsRegisteringWebPush(true)

        try {
            const permission = await appState.requestWebPushPermission()

            if (permission === 'denied' || permission === 'unsupported') {
                await showWebPushPermissionDialog(permission)
            }
        } finally {
            setIsRegisteringWebPush(false)
        }
    }

    if (!isNotificationsVisible) {
        return null
    }

    const WebPushIcon = appState.webPushPermission === 'granted' ? BellCheckIcon : BellOffIcon

    return (
        <aside
            className={classNames(
                'border-border bg-background flex h-full w-96 max-w-[calc(100vw-4rem)] shrink-0 flex-col border-l',
                'sm:w-96 xl:w-[28rem]'
            )}
        >
            <header className="px-page relative flex h-16 shrink-0 items-center justify-between gap-2">
                <h2 className="truncate text-base font-semibold">{t(`${i18nKey}.title`)}</h2>
                <div className="flex shrink-0 items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => void markAllRead()}
                        disabled={isMarkingAllRead || selectedCategories.length === 0}
                        title={t(`${i18nKey}.markAllRead`)}
                        data-title-direction="bottom"
                    >
                        <CheckCheckIcon />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => void handleWebPushClick()}
                        disabled={isRegisteringWebPush}
                        title={t(`utils.webPush.${appState.webPushPermission}.title`)}
                        data-title-direction="bottom"
                    >
                        <WebPushIcon />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                            appState.isNotificationsVisible = false
                        }}
                        title={t('common.close')}
                        data-title-direction="bottom"
                    >
                        <XIcon />
                    </Button>
                </div>
            </header>

            <div className="border-border -mt-2 flex items-center justify-start gap-3 border-b px-3 pb-1 pt-0">
                <Select value={selectedCategory} onValueChange={handleSelectedCategoryChange}>
                    <SelectTrigger
                        size="sm"
                        className={classNames(
                            buttonVariants({ variant: 'ghost', size: 'sm' }),
                            'dark:hover:bg-muted/50 max-w-full justify-start border-transparent shadow-none dark:bg-transparent'
                        )}
                    >
                        <SelectValue>{selectedCategoryLabel}</SelectValue>
                    </SelectTrigger>
                    <SelectContent align="start">
                        <SelectGroup>
                            <SelectItem value={allCategoriesValue}>{t(`${i18nKey}.all`)}</SelectItem>
                            {categories.map((category) => (
                                <SelectItem key={category} value={category}>
                                    {t(`entities.notification.category.${category}`)}
                                </SelectItem>
                            ))}
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                {isLoading && !notifications.length ? (
                    <div className="text-muted-foreground text-sm">{t(`${i18nKey}.loading`)}</div>
                ) : notifications.length === 0 ? (
                    <div className="text-muted-foreground flex flex-col items-center gap-3 py-12 text-center text-sm">
                        <InboxIcon className="size-8" />
                        {t(`${i18nKey}.empty`)}
                    </div>
                ) : (
                    <>
                        <ItemGroup>
                            {notifications.map((notification) => (
                                <NotificationItem
                                    key={getNotificationId(notification)}
                                    notification={notification}
                                    onActivateLink={activateLink}
                                    onChangeReadStatus={changeReadStatus}
                                    onDelete={deleteNotification}
                                />
                            ))}
                        </ItemGroup>
                        {hasMore && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-4 w-full"
                                disabled={isLoading}
                                onClick={() => void notificationsState.loadMore()}
                            >
                                {t(`${i18nKey}.loadMore`)}
                            </Button>
                        )}
                    </>
                )}
            </div>

            <div className="border-border text-muted-foreground border-t px-5 py-3 text-xs">
                {count !== null
                    ? t(`${i18nKey}.countShowing`, { shown: notifications.length, count })
                    : t(`${i18nKey}.count`, { count: notifications.length })}
            </div>
        </aside>
    )
})
